import { Loader2, Sparkles, CheckCircle, Bug, Bookmark, BookmarkCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { API, axios } from "@/api";
import { useOfferDetail } from "./OfferDetailContext";
import { trackWantedPromoAdded } from "@/lib/events";

export default function OfferActionButtons() {
  const {
    offer, cc, tracker, userHasVerified, submitting,
    handleTracker, handleClaimAndVerify, requireAuth, setShowBugReport,
  } = useOfferDetail();
  const [wantedAdded, setWantedAdded] = useState(false);

  const handleAddWanted = () => {
    requireAuth("follow", async () => {
      try {
        const res = await axios.post(`${API}/wanted-promos`, {
          business_id: offer.business_id,
          source: "detail_page_manual",
        });
        if (res.data?.status === "added" || res.data?.status === "exists") {
          setWantedAdded(true);
          trackWantedPromoAdded(offer.business_id, offer.category || null, "detail_page");
          toast.success(res.data.status === "exists" ? "Already in your Wanted list" : "Added to Wanted Promos");
        }
      } catch {
        toast.error("Couldn't add");
      }
    });
  };

  return (
    <>
      {/* FULL-WIDTH ACTION BUTTONS — category gradient */}
      <div className="space-y-2.5" style={{ marginBottom: 'var(--card-gap)' }} data-testid="action-buttons">
        {offer.offer_requires_app && (offer.deal_status === "active" || offer.deal_status === "upcoming") && !tracker?.claimed && !userHasVerified ? (
          <>
            {!tracker ? (
              <button onClick={() => requireAuth("claim", () => handleTracker("signup"))} className={`w-full py-3.5 rounded-xl ${cc.gradient} text-white font-bold shadow-lg text-sm`} data-testid="tracker-signup-btn">Add to Tracker</button>
            ) : (
              <button
                onClick={() => requireAuth("claim", handleClaimAndVerify)}
                disabled={submitting}
                className={`w-full py-3.5 rounded-xl ${cc.gradient} text-white font-bold shadow-lg text-sm flex items-center justify-center gap-2`}
                data-testid="claim-verify-btn"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    I Got Mine — Verify!
                  </>
                )}
              </button>
            )}
          </>
        ) : (
          <>
            {!tracker ? (
              <button onClick={() => requireAuth("claim", () => handleTracker("signup"))} className={`w-full py-3.5 rounded-xl ${cc.gradient} text-white font-bold shadow-lg text-sm`} data-testid="tracker-signup-btn">Add to Tracker</button>
            ) : !tracker.claimed ? (
              <button onClick={() => requireAuth("claim", () => handleTracker("claimed"))} className={`w-full py-3.5 rounded-xl ${cc.gradient} text-white font-bold shadow-lg text-sm`} data-testid="tracker-claim-btn">Mark Claimed</button>
            ) : userHasVerified ? (
              <button className="w-full py-3.5 rounded-xl bg-emerald-100 text-emerald-600 font-bold text-sm flex items-center justify-center gap-1.5" disabled data-testid="already-verified-btn">
                <CheckCircle className="w-4 h-4" /> Claimed & Verified
              </button>
            ) : (
              <button className="w-full py-3.5 rounded-xl bg-slate-200 text-slate-500 font-bold text-sm" disabled>Already Claimed</button>
            )}
          </>
        )}
      </div>

      {/* v1.0.0 Gate 4 — Add to Wanted List (visible for all offers) */}
      <button
        onClick={handleAddWanted}
        disabled={wantedAdded}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-semibold transition-colors ${
          wantedAdded
            ? "bg-emerald-50 text-emerald-600"
            : "bg-slate-100 hover:bg-slate-200 text-slate-700"
        }`}
        data-testid="add-to-wanted-list-btn"
      >
        {wantedAdded ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
        {wantedAdded ? "In Wanted Promos" : "Add to Wanted List"}
      </button>

      {/* Report Issue Button */}
      <button
        onClick={() => setShowBugReport(true)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500 text-xs font-semibold transition-colors"
        data-testid="report-issue-btn"
      >
        <Bug className="w-3.5 h-3.5" /> Report an Issue (+10 pts)
      </button>
    </>
  );
}
